import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './auth';
import { useSolicitacaoAcesso } from '../hooks/useSolicitacaoAcesso';

interface SolicitacaoNotificacao {
  solicitacao_id: number;
  solicitante_id?: number;
  solicitante_nome?: string;
  usuario_alvo_id?: number;
  usuario_alvo_nome?: string;
  token_acesso?: string;
  message: string;
  timestamp: number;
}

interface SolicitacaoAcessoContextData {
  pendentesCount: number;
  notificacoes: SolicitacaoNotificacao[];
  isConnected: boolean;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  clearNotificacoes: () => void;
}

const SolicitacaoAcessoContext = createContext<SolicitacaoAcessoContextData>(
  {} as SolicitacaoAcessoContextData,
);

interface SolicitacaoAcessoProviderProps {
  children: ReactNode;
}

export function SolicitacaoAcessoProvider({ children }: SolicitacaoAcessoProviderProps) {
  const { signed } = useAuth();
  const { getPendentes } = useSolicitacaoAcesso();
  const [notificacoes, setNotificacoes] = useState<SolicitacaoNotificacao[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Query para buscar solicitações pendentes reais
  const { data: pendentesData } = useQuery({
    queryKey: ['solicitacoes-pendentes'],
    queryFn: getPendentes,
    enabled: signed,
    refetchInterval: 120000, // Fallback — WS invalida em tempo real
  });

  const solicitacoesPendentes = pendentesData?.data ?? [];
  const pendentesCountReal = solicitacoesPendentes.length;

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const clearNotificacoes = useCallback(() => {
    setNotificacoes([]);
  }, []);

  // Limpa notificacoes quando modal e fechado
  useEffect(() => {
    if (!isModalOpen) {
      // Aguarda um pouco antes de limpar para dar tempo do usuario ver
      const timeout = setTimeout(() => {
        clearNotificacoes();
      }, 500);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isModalOpen, clearNotificacoes]);

  const contextValue = useMemo(
    () => ({
      pendentesCount: pendentesCountReal, // Usa a contagem real das solicitações pendentes
      notificacoes,
      isConnected: false,
      isModalOpen,
      openModal,
      closeModal,
      clearNotificacoes,
    }),
    [pendentesCountReal, notificacoes, isModalOpen, openModal, closeModal, clearNotificacoes],
  );

  return (
    <SolicitacaoAcessoContext.Provider value={contextValue}>
      {children}
    </SolicitacaoAcessoContext.Provider>
  );
}

export function useSolicitacaoAcessoContext() {
  const context = useContext(SolicitacaoAcessoContext);

  if (!context) {
    throw new Error(
      'useSolicitacaoAcessoContext deve ser usado dentro de SolicitacaoAcessoProvider',
    );
  }

  return context;
}
